import { NARROW_MARKET_SELECTOR_PANEL_MAX_WIDTH } from '@src/components/MarketSelector/tokens';
import { SymbolSelector } from '@src/components/SymbolSelector';
import { useSymbolSelectorWidth } from '@src/components/SymbolSelector/useSymbolSelectorWidth';
import { DeepDiveMarketSelector } from '@src/containers/Trading/DeepDive/Header/DeepDiveMarketSelector';
import { useDeepDiveContext } from '@src/providers/DeepDiveProvider';
import {
  Box,
  canViewDeepDive,
  FormControlSizes,
  HStack,
  IconButton,
  IconName,
  isMultileg,
  MixpanelEvent,
  MOBILE_HEADER_TITLE_PORTAL,
  MobileHeaderSpacer,
  Portal,
  Stack,
  SymbolSelectorAppearance,
  ToolbarButtonWrapper,
  useMixpanel,
  useSecuritiesContext,
  type Security,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';

export function MobileSymbolDeepDiveHeader({ security }: { security: Security }) {
  const mixpanel = useMixpanel();
  const { searchableSecurities } = useSecuritiesContext();
  const { setSymbol, closeDeepDive } = useDeepDiveContext();
  const dropdownWidth = useSymbolSelectorWidth();

  const availableSecurities = useMemo(
    () => searchableSecurities?.filter(s => canViewDeepDive(s) && !isMultileg(s)),
    [searchableSecurities]
  );

  const handleChangeSymbol = useCallback(
    (symbol?: string) => {
      if (symbol) {
        mixpanel.track(MixpanelEvent.ChangeSymbol);
        setSymbol(symbol);
      }
    },
    [mixpanel, setSymbol]
  );

  return (
    <>
      <Portal portalId={MOBILE_HEADER_TITLE_PORTAL}>
        <HStack gap="spacing8" flex={1} minWidth={0} w="100%">
          <ToolbarButtonWrapper>
            <IconButton
              data-testid="mobile-symbol-deep-dive-back-button"
              ghost
              icon={IconName.ArrowLeft}
              onClick={closeDeepDive}
            />
          </ToolbarButtonWrapper>
          <Box flex={1} minWidth={0} overflow="hidden">
            <SymbolSelector
              appearance={SymbolSelectorAppearance.Ghost}
              symbol={security.Symbol}
              availableSecurities={availableSecurities}
              onSymbolChanged={handleChangeSymbol}
              startOpen={false}
              portalize
              dropdownWidth={dropdownWidth}
              maxHeight={480}
              showGroups
              showFavorites
              dropdownId="mobile-symbol-deep-dive-symbol-selector-dropdown"
              id="mobile-symbol-deep-dive-symbol-selector"
            />
          </Box>
        </HStack>
      </Portal>
      <MobileHeaderSpacer data-testid="mobile-symbol-deep-dive-header-spacer" />
      <Stack background="backgroundContent" borderBottom="1px solid" borderColor="backgroundDivider" p="spacing16">
        <DeepDiveMarketSelector
          security={security}
          isNarrow
          panelOffset={0}
          panelMaxWidth={NARROW_MARKET_SELECTOR_PANEL_MAX_WIDTH}
          size={FormControlSizes.Default}
        />
      </Stack>
    </>
  );
}
